import { DestroyRef, inject, Signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DateTime } from 'luxon';
import { distinctUntilChanged, filter, map, take } from 'rxjs';
import { InteractionRule, TagForInteractionRule } from '@origin8-web/models/call-reviews';
import { DEFAULT_DATE_RANGE_PRESET, dateRangeForPreset, toWholeLocalDayRange } from './date-range-presets';

/** Query param keys that are shared across all pages and should persist on navigation. */
const SHARED_QUERY_PARAMS = ['startDate', 'endDate', 'tags', 'columnFilters'] as const;

/**
 * Picks only shared query params from the given params object.
 * Use this when navigating between pages to avoid leaking page-specific state.
 */
export function pickSharedQueryParams(params: Params): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key of SHARED_QUERY_PARAMS) {
    if (params[key] != null) {
      result[key] = params[key];
    }
  }
  return result;
}

/** Params written before the range carried a time. Still shared, bookmarked, and hand-typed. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * One end of the range, as an absolute instant. A param with a time is taken exactly as written, so
 * a shared link picks out the same records in every timezone; a bare date means that whole local day.
 */
function parseRangeBound(raw: string | undefined, bound: 'start' | 'end'): Date | null {
  if (!raw) return null;
  const parsed = DateTime.fromISO(raw);
  if (!parsed.isValid) return null;
  if (!DATE_ONLY.test(raw)) return parsed.toJSDate();
  return (bound === 'start' ? parsed.startOf('day') : parsed.endOf('day')).toJSDate();
}

/**
 * Reads startDate/endDate from the current route's query params.
 * Returns null if either param is missing or invalid.
 */
export function readDateRangeFromQueryParams(route: ActivatedRoute): { startDate: Date; endDate: Date } | null {
  const params = route.snapshot.queryParams;
  const startDate = parseRangeBound(params['startDate'], 'start');
  const endDate = parseRangeBound(params['endDate'], 'end');
  if (!startDate || !endDate) return null;

  return { startDate, endDate };
}

/**
 * The date range currently in the URL, as a signal — the range the whole supervisor view works from.
 *
 * Reading the snapshot once (see `readDateRangeFromQueryParams`) is enough for a page that owns its
 * own picker, but not when the control lives on a parent that stays mounted while tabs come and go:
 * the tab has to react to a change made above it. Falls back to the default window whenever the
 * params are absent or unparseable, so "no params" and "reset" resolve to the same range.
 *
 * Must be called from an injection context.
 */
export function injectDateRangeFromQueryParams(): Signal<{ startDate: Date; endDate: Date }> {
  const route = inject(ActivatedRoute);

  // Same default the period dropdown shows when nothing is chosen, so the two can't disagree.
  const fallback = (): { startDate: Date; endDate: Date } =>
    dateRangeForPreset(DEFAULT_DATE_RANGE_PRESET) ?? {
      startDate: DateTime.now().startOf('month').toJSDate(),
      endDate: DateTime.now().toJSDate(),
    };

  const parse = (params: Params): { startDate: Date; endDate: Date } => {
    const startDate = parseRangeBound(params['startDate'], 'start');
    const endDate = parseRangeBound(params['endDate'], 'end');
    if (!startDate || !endDate) return fallback();
    return { startDate, endDate };
  };

  const sameRange = (a: { startDate: Date; endDate: Date }, b: { startDate: Date; endDate: Date }) =>
    a.startDate.getTime() === b.startDate.getTime() && a.endDate.getTime() === b.endDate.getTime();

  return toSignal(route.queryParams.pipe(map(parse), distinctUntilChanged(sameRange)), {
    initialValue: parse(route.snapshot.queryParams),
  });
}

/**
 * Merges the given params into the current route's query params.
 * Pass null for a param value to remove it.
 *
 * By default the URL fragment (e.g. `#utterance=…`) is dropped, matching Angular's default. Pass
 * `{ preserveFragment: true }` to keep it — needed when reflecting the open transcript in the URL so
 * a shared deep link to a specific utterance survives the query-param write.
 */
export function updateQueryParams(
  router: Router,
  route: ActivatedRoute,
  params: Record<string, string | null>,
  options: { preserveFragment?: boolean } = {},
): void {
  void router.navigate([], {
    relativeTo: route,
    queryParams: params,
    queryParamsHandling: 'merge',
    preserveFragment: options.preserveFragment ?? false,
    replaceUrl: true,
  });
}

/**
 * Formats a date range as query param values: instants with an offset, covering whole local days.
 * Bare `yyyy-MM-dd` meant whichever day that was on the reader's clock, so a link shared from London
 * and opened in New York answered with different calls.
 */
export function formatDateRangeParams(startDate: Date, endDate: Date): { startDate: string; endDate: string } {
  const range = toWholeLocalDayRange(startDate, endDate);
  return {
    startDate: DateTime.fromJSDate(range.startDate).toISO()!,
    endDate: DateTime.fromJSDate(range.endDate).toISO()!,
  };
}

/**
 * Clears all filter-related query params and returns the default date range.
 * The caller is responsible for patching its own store with the returned dates.
 */
export function resetFilterQueryParams(
  router: Router,
  route: ActivatedRoute,
  defaultDateRange: { months?: number; days?: number } = { months: 1 },
): { startDate: Date; endDate: Date } {
  const now = DateTime.now();
  const defaultStart = defaultDateRange.days
    ? now.minus({ days: defaultDateRange.days }).toJSDate()
    : now.minus({ months: defaultDateRange.months ?? 1 }).toJSDate();

  updateQueryParams(router, route, { startDate: null, endDate: null, tags: null, columnFilters: null });

  // Through the whole of today, not up to this moment.
  return toWholeLocalDayRange(defaultStart, now.toJSDate());
}

/**
 * Restores selected tags from the 'tags' query param once rules are loaded.
 * Must be called from an injection context (e.g. constructor).
 */
export function restoreSelectedTagsFromQueryParams(
  route: ActivatedRoute,
  rules: Signal<InteractionRule[]>,
  setSelectedTags: (tags: TagForInteractionRule[]) => void,
): void {
  const tagsParam = route.snapshot.queryParams['tags'];
  if (!tagsParam) return;

  const destroyRef = inject(DestroyRef);
  const tagIds = new Set<string>(tagsParam.split(','));
  toObservable(rules)
    .pipe(
      filter((r) => r.length > 0),
      take(1),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe((loadedRules) => {
      const seen = new Set<string>();
      const selectedTags = loadedRules
        .flatMap((r) => r.outputTags)
        .filter((t) => {
          if (!tagIds.has(t.tagDefinitionId) || seen.has(t.tagDefinitionId)) return false;
          seen.add(t.tagDefinitionId);
          return true;
        });
      if (selectedTags.length > 0) {
        setSelectedTags(selectedTags);
      }
    });
}
